import { cloneElement, useEffect, type ReactNode } from "react";
import useFormWizard from "./FormWizardProvider";
import PageFormRenderer from "./PageFormRenderer/PageFormRenderer";
import InlineFormRenderer from "./InlineFormRenderer/InlineFormRenderer";
import { formVariantsEnum } from "./FormWizard";
import {
  showDialog,
  type NavigationBreadcrumbItem,
} from "@SeemplicityDev/shared";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import type { FormVariantType, FormWizardData, FormWizardStep } from "./types";

interface FormVariantProps {
  className?: string;
  type: FormVariantType;
  children: ReactNode;
  breadcrumbs?: NavigationBreadcrumbItem[];
}

const FormVariant: React.FC<FormVariantProps> = ({
  className,
  type,
  children,
  breadcrumbs,
}) => {
  if (type === formVariantsEnum.PAGE) {
    return (
      <PageFormRenderer className={className} breadcrumbs={breadcrumbs}>
        {children}
      </PageFormRenderer>
    );
  }

  return (
    <InlineFormRenderer className={className}>{children}</InlineFormRenderer>
  );
};

interface FormRendererProps {
  steps: FormWizardStep[];
  onCancel: () => void;
  onSubmit: (formData: FormWizardData) => void;
  isSubmitting?: boolean;
  dangerSubmit?: boolean;
  className?: string;
  type: FormVariantType;
  originLink?: string;
  isValidated?: boolean;
  originName?: string;
  wizardName: string;
  wizardData?: FormWizardData;
  withSummary?: boolean;
  submitLabel?: string;
  breadcrumbs?: NavigationBreadcrumbItem[];
}

const FormRenderer: React.FC<FormRendererProps> = ({
  steps,
  onCancel,
  onSubmit,
  isSubmitting,
  dangerSubmit,
  className,
  type,
  originLink,
  isValidated,
  originName,
  wizardName,
  wizardData,
  withSummary,
  submitLabel,
  breadcrumbs,
}) => {
  const {
    initFormWizard,
    activeStep,
    handleStepSubmit,
    setSubmitting,
    formData,
    setOnSubmit,
  } = useFormWizard();
  const { t } = useTranslation();
  const history = useHistory();

  const handlePageFormCancel = () => {
    showDialog({
      title: t(`Cancel ${wizardName}`),
      message: (
        <div className={"modal-text"}>
          {t(`You haven't completed all steps. If you leave now, the data will be deleted.\n
Are you sure you want to exit the wizard and cancel the ${wizardName}?`)}
        </div>
      ),
      buttons: [
        {
          "data-testid": "cancel-exit",
          label: "Stay",
        },
        {
          buttonStyle: "danger",
          label: "Leave",
          onClick: () => {
            onCancel();
            if (!!originLink) {
              history.replace(originLink);
            }
          },
        },
      ],
    });
  };

  useEffect(() => {
    setOnSubmit(onSubmit);
  }, [onSubmit]);

  useEffect(() => {
    initFormWizard(
      steps,
      type === formVariantsEnum.PAGE ? handlePageFormCancel : onCancel,
      !!dangerSubmit,
      { link: originLink, name: originName },
      wizardName,
      wizardData,
      !!isValidated,
      !!withSummary,
      type,
      submitLabel
    );
  }, [
    initFormWizard,
    steps,
    originName,
    originLink,
    wizardName,
    isValidated,
    wizardData,
    withSummary,
    type,
    submitLabel,
  ]);

  useEffect(() => {
    setSubmitting(!!isSubmitting);
  }, [isSubmitting]);

  return (
    <>
      {!!activeStep && (
        <FormVariant
          className={className}
          type={type}
          breadcrumbs={breadcrumbs}
        >
          {cloneElement(activeStep.form, {
            onValidate: handleStepSubmit,
            defaultValues: formData,
          })}
        </FormVariant>
      )}
    </>
  );
};

export default FormRenderer;
